import { useState } from "react";
import dayjs from "dayjs";

import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Popover from "react-bootstrap/Popover";
import Table from "react-bootstrap/Table";
import { FaEllipsisV, FaEye, FaInfoCircle } from "react-icons/fa";

import { ResumeCitizenGroupActivitiesIndicatorB4Data } from "@/services/esus/indicator/esb/b4/getResumeCitizensGroupActivitiesIndicatorB4";
import { formatIdentifier } from "@/utils/formatIdentifier";
import { handleColumnToggle } from "@/utils/handleColumnTogle";
import { getAge } from "@/utils/dateUtil";
import { getCitizenScoreStatusDescriptionIndicatorEsb } from "@/utils/indicatorsEsb";

import { TableSkeletons } from "@/components/TableSkeletons";
import { CardOverlayTrigger } from "@/components/CardOverlayTrigger";

type Column = {
	title: string;
	visible: boolean;
};

type IndicatorEsbB4GroupActivityTableColumns = {
	citizenName: Column;
	citizenAge: Column;
	document: Column;
	microarea: Column;
	citizenEsfHealthTeam: Column;
	attendanceEsbHealthTeam: Column;
	attendanceEsfHealthTeam: Column;
	supervisedBrushing: Column;
};

type IndicatorEsbB4GroupActivitiesTableProps = {
	citizensWithGroupActivities: ResumeCitizenGroupActivitiesIndicatorB4Data[];
	isLoading: boolean;
	openCitizenHistory: (citizenId: number, ine?: string) => void;
};

export function IndicatorEsbB4GroupActivitiesTable({
	citizensWithGroupActivities,
	isLoading,
	openCitizenHistory,
}: Readonly<IndicatorEsbB4GroupActivitiesTableProps>) {
	const [columns, setColumns] = useState<IndicatorEsbB4GroupActivityTableColumns>({
		citizenName: { title: "Nome do cidadão", visible: true },
		citizenAge: { title: "Idade", visible: true },
		document: { title: "Documento", visible: true },
		microarea: { title: "MA", visible: true },
		citizenEsfHealthTeam: { title: "Equipe ESF Cidadão", visible: true },
		attendanceEsbHealthTeam: { title: "Equipe ESB Atendimento", visible: true },
		attendanceEsfHealthTeam: { title: "Equipe ESF vinculada a ESB Atendimento", visible: true },
		supervisedBrushing: { title: "Escovação supervisionada", visible: true },
	});

	return (
		<Table striped bordered hover responsive>
			<thead className="text-center fs-7">
				<tr className="fw-bolder text-center">
					<th className={`text-start px-2 ${!columns.citizenName.visible && "d-none"}`}>
						{columns.citizenName.title}
					</th>
					<th className={`px-2 ${!columns.citizenAge.visible && "d-none"}`}>
						{columns.citizenAge.title}
					</th>
					<th className={`px-2 ${!columns.document.visible && "d-none"}`}>
						{columns.document.title}
					</th>
					<th className={`px-2 ${!columns.microarea.visible && "d-none"}`}>
						{columns.microarea.title}
					</th>
					<th
						className={`text-start px-2 ${
							!columns.citizenEsfHealthTeam.visible && "d-none"
						}`}
					>
						{columns.citizenEsfHealthTeam.title}
					</th>
					<th
						className={`text-start px-2 ${
							!columns.attendanceEsbHealthTeam.visible && "d-none"
						}`}
					>
						{columns.attendanceEsbHealthTeam.title}
					</th>
					<th
						className={`text-start px-2 ${
							!columns.attendanceEsfHealthTeam.visible && "d-none"
						}`}
					>
						{columns.attendanceEsfHealthTeam.title}
					</th>
					<th className={`px-2 ${!columns.supervisedBrushing.visible && "d-none"}`}>
						<div className="d-flex flex-column">
							{columns.supervisedBrushing.title}
							<CardOverlayTrigger
								header="Escovação supervisionada"
								message="Cidadão que participou de atividade coletiva de escovação supervisionada entre 6 e 12 anos de idade"
								icon={<FaInfoCircle className="text-muted" />}
							/>
						</div>
					</th>
					<th className="text-nowrap pb-0">
						Ações
						<OverlayTrigger
							trigger="click"
							placement="bottom"
							overlay={
								<Popover>
									<Popover.Body>
										<Form className="d-flex flex-column gap-2">
											{Object.keys(columns).map((key, index) => (
												<Form.Check
													key={`citizen-groupActivitys-table-column-${
														index + 1
													}`}
													type="checkbox"
													label={
														columns[
															key as keyof IndicatorEsbB4GroupActivityTableColumns
														].title
													}
													checked={
														columns[
															key as keyof IndicatorEsbB4GroupActivityTableColumns
														].visible
													}
													onChange={() =>
														handleColumnToggle(
															key as keyof IndicatorEsbB4GroupActivityTableColumns,
															setColumns
														)
													}
												/>
											))}
										</Form>
									</Popover.Body>
								</Popover>
							}
							rootClose
						>
							<Button
								title="Exibição de colunas da tabela"
								variant="white"
								className="px-2"
								disabled={isLoading}
							>
								<FaEllipsisV />
							</Button>
						</OverlayTrigger>
					</th>
				</tr>
			</thead>
			<tbody className="fs-8">
				{!isLoading && citizensWithGroupActivities.length > 0
					? citizensWithGroupActivities.map((citizenWithGroupActivities, index) => {
							return (
								<tr key={`citizen-groupActivitys-table-row-${index + 1}`}>
									<td
										className={`px-2 ${
											!columns.citizenName.visible && "d-none"
										}`}
									>
										<div className="d-flex flex-column">
											<div>{citizenWithGroupActivities.fullName}</div>
											<div className="text-muted">
												{citizenWithGroupActivities.birthdate
													? dayjs(
															citizenWithGroupActivities.birthdate
													  ).format("DD/MM/YYYY")
													: ""}
											</div>
										</div>
									</td>
									<td
										className={`text-center text-nowrap px-2 ${
											!columns.citizenAge.visible && "d-none"
										}`}
									>
										{citizenWithGroupActivities.birthdate
											? getAge(citizenWithGroupActivities.birthdate)
											: ""}
									</td>
									<td
										className={`text-center text-nowrap px-2 ${
											!columns.document.visible && "d-none"
										}`}
									>
										{citizenWithGroupActivities.cpf
											? formatIdentifier(citizenWithGroupActivities.cpf)
											: citizenWithGroupActivities.cns ?? ""}
									</td>
									<td
										className={`text-center text-nowrap px-2 ${
											!columns.microarea.visible && "d-none"
										}`}
									>
										{citizenWithGroupActivities.area ?? "FA"}
									</td>
									<td
										className={`px-2 ${
											!columns.citizenEsfHealthTeam.visible && "d-none"
										}`}
									>
										<div className="d-flex flex-column">
											<div>{citizenWithGroupActivities.healthTeamName}</div>
											<div className="text-muted">
												{citizenWithGroupActivities.healthTeamIne}
											</div>
										</div>
									</td>
									<td
										className={`px-2 ${
											!columns.attendanceEsbHealthTeam.visible && "d-none"
										}`}
									>
										<div className="d-flex flex-column">
											<div>
												{citizenWithGroupActivities.esbHealthTeamName}
											</div>
											<div className="text-muted">
												{citizenWithGroupActivities.esbHealthTeamIne}
											</div>
										</div>
									</td>
									<td
										className={`px-2 ${
											!columns.attendanceEsfHealthTeam.visible && "d-none"
										}`}
									>
										<div className="d-flex flex-column">
											<div>
												{citizenWithGroupActivities.linkedHealthTeamName}
											</div>
											<div className="text-muted">
												{citizenWithGroupActivities.linkedHealthTeamIne}
											</div>
										</div>
									</td>
									<td
										className={`text-center px-2 ${
											!columns.supervisedBrushing.visible && "d-none"
										}`}
									>
										{getCitizenScoreStatusDescriptionIndicatorEsb(
											citizenWithGroupActivities.supervisedBrushingStatus
										)}
									</td>
									<td className="text-center">
										<Button
											className="bg-primary py-1 px-3"
											onClick={() =>
												openCitizenHistory(
													citizenWithGroupActivities.id,
													citizenWithGroupActivities.esbHealthTeamIne
												)
											}
											title="Histórico do cidadão"
										>
											<FaEye className="fs-4" />
										</Button>
									</td>
								</tr>
							);
					  })
					: isLoading && (
							<TableSkeletons
								numberOfCells={
									Object.values(columns).reduce(
										(count, column) => count + (column?.visible ? 1 : 0),
										0
									) + 1
								}
							/>
					  )}
			</tbody>
		</Table>
	);
}
